import { IOrderFetch } from 'src/models/IOrder';
import { IVanFetch } from 'src/models/IVan';
import { colorSet } from 'src/theme';

const cutText = (text: string, amount: number) =>
  text.length > amount ? `${text.slice(0, amount)}...` : text;

const getNames = (dataArray: any[]) => dataArray.map(({ name }) => name);

const getNamesWithCommas = (dataArray: any[]) => {
  const array = dataArray.map(({ name }) => name);
  return array.join(', ');
};

const getListHoursAMPM = () => {
  const minuteArray: string[] = [];
  ['AM', 'PM'].forEach(time => {
    minuteArray.push(time === 'AM' ? 'Midnight' : 'Noon');
    minuteArray.push(`12:30 ${time}`);
    for (let j = 1; j < 12; j++) {
      ['00', '30'].forEach(minute => minuteArray.push(`${j}:${minute} ${time}`));
    }
  });
  return minuteArray;
};

const randomColor = (id: number) => {
  const colors = [
    '#d24057',
    '#c04fff',
    '#151c97',
    '#4b566b',
    '#DE7444',
    '#2E966F',
    '#8980ed',
    '#00ffaa',
    '#c1ff45',
    '#091b3d'
  ];
  const index = id > 9 ? id % 10 : id;
  return colors[index];
};

class EnumHelpers {
  static getNamesAndValues<T extends number>(e: any) {
    return EnumHelpers.getNames(e).map(n => ({ name: n, value: e[n] as T }));
  }

  static getNames(e: any) {
    return EnumHelpers.getObjValues(e).filter(v => typeof v === 'string') as string[];
  }

  static getValues<T extends number>(e: any) {
    return EnumHelpers.getObjValues(e).filter(v => typeof v === 'number') as T[];
  }

  static getSelectList<T extends number, U>(e: any, stringConverter: (arg: U) => string) {
    const selectList = new Map<T, string>();
    this.getValues(e).forEach(val =>
      selectList.set(val as T, stringConverter(val as unknown as U))
    );
    return selectList;
  }

  static getSelectListAsArray<T extends number, U>(e: any, stringConverter: (arg: U) => string) {
    return Array.from(this.getSelectList(e, stringConverter), value => ({
      value: value[0] as T,
      presentation: value[1]
    }));
  }

  private static getObjValues(e: any): (number | string)[] {
    return Object.keys(e).map(k => e[k]);
  }
}

const colorThemeOrder = (order: IOrderFetch) => {
  const {
    id,
    pdf_order,
    client: { driver_license },
    isOneway,
    status
  } = order;
  if (status === 'leads') return { background: colorSet.primaryGrey };
  if (status === 'deleted') return { background: 'rgba(255, 0, 0, 0.7)' };
  if (pdf_order && status === 'paid' && driver_license) return { background: colorSet.thirdGreen };
  if (status === 'paid' && driver_license) return { background: colorSet.primaryGreen };
  if (driver_license && !isOneway) return { background: colorSet.primaryBabyblue };
  if (isOneway) return { background: colorSet.primaryBiolet };
  if (status === 'blocked') return { background: colorSet.palePink };
  return { background: colorSet.primaryOrange };
};

type IFilterVan = Pick<IVanFetch, 'id' | 'location' | 'make' | 'type' | 'year'>;

const BASE_URL = 'https://van.sprinter-rentals.com/van';
const PREFIX = '143';

const normalize = (value?: string): string => {
  if (!value) {
    return '';
  }

  return value
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const toSegment = (value?: string, fallback = '-'): string => {
  const cleaned = normalize(value).replaceAll(' ', '');
  return cleaned || fallback;
};

const createVanURL = (filter: IFilterVan): string => {
  if (!filter?.id || !filter?.location) {
    return '';
  }

  const [rawState, rawCity] = filter.location.split(' - ');

  const state = toSegment(rawState);
  const city = toSegment(rawCity, state);

  const make = toSegment(filter.make);
  const type = toSegment(filter.type);
  const year = toSegment(filter.year);

  const url = `${BASE_URL}/${state}/${city}/${make}/${type}/${year}/${PREFIX}${filter.id}`;

  return url.replace(/([^:]\/)\/+/g, '$1');
};

export {
  cutText,
  getNames,
  getListHoursAMPM,
  randomColor,
  EnumHelpers,
  colorThemeOrder,
  getNamesWithCommas,
  createVanURL
};
